import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Sparkles, Send, ArrowRight, ThumbsUp, ThumbsDown, Loader2 } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { InlineAlert } from '@/components/shared/InlineAlert';
import { askHelp, submitHelpFeedback, type HelpAnswer } from '@/api/help';
import { ApiRequestError } from '@/types/api';
import { useOnboarding, pickNextActionableStep } from '@/hooks/useOnboarding';
import { suggestedQuestions } from './askAiSuggestions';
import { cn } from '@/lib/utils';

const MAX_QUESTION_CHARS = 500;

interface AskAiPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Turn a failure into something the user can act on, not a status code. */
function errorMessage(error: unknown): string {
  if (error instanceof ApiRequestError) {
    if (error.code === 'HELP_DAILY_LIMIT_REACHED' || error.status === 429) {
      return error.message;
    }
    if (error.code === 'HELP_NOT_CONFIGURED') {
      return 'Ask AI is not available right now. Email support@gateco.ai if you need a hand.';
    }
    return error.message;
  }
  return 'Something went wrong. Please try again.';
}

export function AskAiPanel({ open, onOpenChange }: AskAiPanelProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [question, setQuestion] = useState('');
  const [asked, setAsked] = useState<string | null>(null);
  const [voted, setVoted] = useState<boolean | null>(null);

  const { data: onboarding } = useOnboarding();
  const nextStep = onboarding ? pickNextActionableStep(onboarding.data.steps) : null;
  const suggestions = suggestedQuestions(location.pathname, nextStep);

  const mutation = useMutation<HelpAnswer, unknown, string>({
    mutationFn: (q: string) => askHelp({ question: q, route: location.pathname }),
  });

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 80);
  }, [open]);

  const submit = (value: string) => {
    const trimmed = value.trim();
    if (trimmed.length < 3 || mutation.isPending) return;
    setAsked(trimmed);
    setVoted(null);
    mutation.mutate(trimmed);
  };

  const result = mutation.data;

  const vote = async (helpful: boolean) => {
    if (!result?.question_id || voted !== null) return;
    setVoted(helpful);
    try {
      await submitHelpFeedback(result.question_id, helpful);
    } catch {
      // Feedback is best-effort; never interrupt the user over analytics.
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="gap-0 p-0">
        <div className="flex items-center gap-2 border-b border-border px-5 py-4">
          <Sparkles className="h-4 w-4 text-primary" />
          <div className="min-w-0">
            <SheetTitle>Ask AI</SheetTitle>
            <SheetDescription>
              Questions about setting up and using Gateco.
            </SheetDescription>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {!asked && (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">
                Not sure where to start? Try one of these.
              </p>
              {suggestions.map((s) => (
                <button
                  key={s}
                  onClick={() => submit(s)}
                  className="w-full rounded-md border border-border bg-card px-3 py-2.5 text-left text-sm text-foreground transition-colors hover:border-primary/50 hover:bg-accent"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {asked && (
            <div className="space-y-4">
              <p className="text-sm font-medium text-foreground">{asked}</p>

              {mutation.isPending && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Looking through the Gateco docs...
                </div>
              )}

              {mutation.isError && (
                <InlineAlert variant="destructive" title="Could not answer that">
                  {errorMessage(mutation.error)}
                </InlineAlert>
              )}

              {result && (
                <div className="space-y-4">
                  {result.answer && (
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                      {result.answer}
                    </p>
                  )}

                  {result.steps.length > 0 && (
                    <ol className="space-y-2">
                      {result.steps.map((step, i) => (
                        <li key={i} className="flex gap-2.5 text-sm text-foreground">
                          <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/15 text-[11px] font-medium text-primary">
                            {i + 1}
                          </span>
                          <span className="leading-relaxed">{step}</span>
                        </li>
                      ))}
                    </ol>
                  )}

                  {result.deep_link && (
                    <Button
                      size="sm"
                      onClick={() => {
                        navigate(result.deep_link as string);
                        onOpenChange(false);
                      }}
                    >
                      Take me there
                      <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                    </Button>
                  )}

                  {result.citations.length > 0 && (
                    <div className="border-t border-border pt-3">
                      <p className="mb-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
                        Based on
                      </p>
                      <ul className="space-y-1">
                        {result.citations.map((c) => (
                          <li key={c.chunk_id} className="text-xs text-muted-foreground">
                            {c.section_path}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {result.question_id && (
                    <div className="flex items-center gap-2 border-t border-border pt-3">
                      <span className="text-xs text-muted-foreground">
                        {voted === null ? 'Was this helpful?' : 'Thanks for the feedback.'}
                      </span>
                      <button
                        aria-label="Helpful"
                        disabled={voted !== null}
                        onClick={() => vote(true)}
                        className={cn(
                          'rounded p-1 text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default',
                          voted === true && 'text-primary'
                        )}
                      >
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        aria-label="Not helpful"
                        disabled={voted !== null}
                        onClick={() => vote(false)}
                        className={cn(
                          'rounded p-1 text-muted-foreground transition-colors hover:text-foreground disabled:cursor-default',
                          voted === false && 'text-status-warning'
                        )}
                      >
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(question);
            setQuestion('');
          }}
          className="border-t border-border p-4"
        >
          <div className="flex gap-2">
            <Input
              ref={inputRef}
              value={question}
              maxLength={MAX_QUESTION_CHARS}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ask about connectors, policies, plans..."
              aria-label="Ask a question about Gateco"
            />
            <Button
              type="submit"
              size="icon"
              disabled={question.trim().length < 3 || mutation.isPending}
              aria-label="Send question"
            >
              <Send className="h-4 w-4" />
            </Button>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Answers come from Gateco's documentation. Never paste credentials here.
          </p>
        </form>
      </SheetContent>
    </Sheet>
  );
}
